/**
 * LeaguesPage — `/leagues` — the "My Friend Leagues" index.
 *
 * Three states:
 *
 *   - status === "unknown" : show a small loader while auth hydrates.
 *   - anonymous            : explain what leagues are; show a Sign in CTA.
 *   - signed-in (any)      : show my-leagues list + Create button + Join-by-code form.
 *
 * Free accounts can create up to 3 leagues; Pro is uncapped. The cap is
 * enforced server-side (POST /api/leagues returns 403 with `free_league_limit`);
 * we just surface the friendly error from the create modal.
 *
 * The join-by-code form normalizes input (uppercase, strip whitespace) and
 * accepts the join URL itself (parses the trailing /league/<code>) — so users
 * can paste either form.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, ApiError, type LeagueSummary } from "@/api/client";
import { useAuthStore } from "@/store/authStore";
import { flag, resolveCountry } from "@/lib/countries";
import { CreateLeagueModal } from "@/components/CreateLeagueModal";
import { SignInModal } from "@/components/SignInModal";

type LeagueListRow = LeagueSummary & { joinedAt?: string };

const CODE_RE = /([23456789ABCDEFGHJKMNPQRTUVWXYZ]{6})/i;

function extractCode(input: string): string | null {
  const trimmed = input.trim();
  if (!trimmed) return null;
  // Try as URL fragment first (lets users paste a join URL).
  const m = trimmed.match(CODE_RE);
  return m ? m[1]!.toUpperCase() : null;
}

export function LeaguesPage() {
  const { t } = useTranslation();
  const status = useAuthStore((s) => s.status);
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();

  const [leagues, setLeagues] = useState<LeagueListRow[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [signInOpen, setSignInOpen] = useState(false);
  const [joinInput, setJoinInput] = useState("");
  const [joinError, setJoinError] = useState<string | null>(null);
  const [joinBusy, setJoinBusy] = useState(false);

  // Fetch my leagues when signed in.
  useEffect(() => {
    if (status !== "authed" || !user?.email) {
      setLeagues(null);
      return;
    }
    setLoading(true);
    api
      .myLeagues()
      .then((r) => setLeagues(r.leagues))
      .catch(() => setLeagues([]))
      .finally(() => setLoading(false));
  }, [status, user?.email]);

  const onJoin = async () => {
    if (joinBusy) return;
    const code = extractCode(joinInput);
    if (!code) {
      setJoinError(
        "That doesn't look like a valid invite code. Paste the 6-character code or the full /league/<code> URL.",
      );
      return;
    }
    setJoinBusy(true);
    setJoinError(null);
    try {
      const { league } = await api.joinLeague(code);
      navigate(`/leagues/${league.id}`);
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string; countryLock?: string } | undefined;
        if (err.status === 404) setJoinError("No league found with that code.");
        else if (err.status === 403 && body?.error === "country_lock") {
          setJoinError(
            `This league is country-locked to ${body.countryLock}. You're in ${user?.countryCode ?? "—"}.`,
          );
        } else if (err.status === 409 && body?.error === "league_full") {
          setJoinError("This league is already full.");
        } else if (err.status === 401) {
          setSignInOpen(true);
        } else {
          setJoinError("Couldn't join — try again in a moment.");
        }
      } else {
        setJoinError("Couldn't join — try again in a moment.");
      }
    } finally {
      setJoinBusy(false);
    }
  };

  // ---- Loading / anonymous states ----

  if (status === "unknown") {
    return (
      <Shell>
        <p className="text-sm opacity-70">Loading…</p>
      </Shell>
    );
  }

  if (status === "anonymous" || !user?.email) {
    return (
      <Shell>
        <h1 className="text-2xl font-bold">{t("leagues.title")}</h1>
        <p className="mt-3 text-sm opacity-80">
          {t("leagues.anon_explainer")}
        </p>
        <p className="mt-2 text-sm opacity-60">
          {t("leagues.anon_signin_hint")}
        </p>
        <div className="mt-5 flex flex-wrap gap-2">
          <button
            type="button"
            className="rounded-full bg-amber-300 px-4 py-2 font-semibold text-emerald-950 hover:bg-amber-200"
            onClick={() => setSignInOpen(true)}
          >
            {t("header.sign_in")}
          </button>
          <Link
            to="/play"
            className="rounded-full bg-white/10 px-4 py-2 font-semibold hover:bg-white/20"
          >
            {t("account.today_kick")}
          </Link>
        </div>
        <SignInModal open={signInOpen} onClose={() => setSignInOpen(false)} />
      </Shell>
    );
  }

  // ---- Signed-in state ----

  return (
    <Shell>
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h1 className="text-2xl font-bold">{t("leagues.title")}</h1>
        <button
          type="button"
          className="rounded-full bg-amber-300 px-4 py-2 text-sm font-semibold text-emerald-950 hover:bg-amber-200"
          onClick={() => setCreateOpen(true)}
        >
          {t("leagues.create_button")}
        </button>
      </div>

      {/* Join-by-code form */}
      <div className="mt-5 rounded-2xl bg-white/5 p-4 ring-1 ring-white/10">
        <p className="text-xs font-semibold uppercase tracking-wider opacity-70">
          {t("leagues.invite_prompt_label")}
        </p>
        <div className="mt-2 flex gap-2">
          <input
            type="text"
            value={joinInput}
            onChange={(e) => setJoinInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") void onJoin();
            }}
            placeholder={t("leagues.invite_placeholder")}
            className="flex-1 rounded-xl bg-white/10 px-3 py-2 text-base outline-none ring-emerald-400/60 focus:ring-2"
          />
          <button
            type="button"
            disabled={joinBusy}
            onClick={onJoin}
            className="rounded-full bg-white/10 px-4 py-2 text-sm font-semibold hover:bg-white/20 disabled:opacity-50"
          >
            {joinBusy ? t("leagues.joining_button") : t("leagues.join_button")}
          </button>
        </div>
        {joinError && <p className="mt-2 text-sm text-rose-300">{joinError}</p>}
      </div>

      {/* My leagues list */}
      <h2 className="mt-6 text-sm font-semibold uppercase tracking-wider opacity-70">
        {t("leagues.your_leagues")}
      </h2>
      {loading ? (
        <p className="mt-3 text-sm opacity-60">{t("common.loading")}</p>
      ) : !leagues || leagues.length === 0 ? (
        <div className="mt-3 rounded-2xl bg-white/5 p-5 ring-1 ring-white/10">
          <p className="text-sm opacity-80">
            {t("leagues.empty_state")}
          </p>
          <button
            type="button"
            className="mt-3 rounded-full bg-amber-300 px-4 py-2 text-sm font-semibold text-emerald-950 hover:bg-amber-200"
            onClick={() => setCreateOpen(true)}
          >
            {t("leagues.create_first")}
          </button>
        </div>
      ) : (
        <ul className="mt-3 space-y-2">
          {leagues.map((l) => (
            <li key={l.id}>
              <Link
                to={`/leagues/${l.id}`}
                className="flex items-center justify-between gap-3 rounded-2xl bg-white/5 p-4 ring-1 ring-white/10 transition hover:bg-white/10 hover:ring-amber-300/30"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="truncate text-base font-semibold">{l.name}</p>
                    {l.myRole === "creator" && (
                      <span className="rounded-full bg-amber-300/20 px-1.5 py-0.5 text-[10px] font-bold uppercase text-amber-200">
                        Creator
                      </span>
                    )}
                    {l.countryLock && (
                      <span
                        className="rounded-full bg-white/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase opacity-70"
                        title={`Locked to ${resolveCountry(l.countryLock).name}`}
                      >
                        {flag(l.countryLock)} {l.countryLock}
                      </span>
                    )}
                  </div>
                  <p className="mt-0.5 text-xs opacity-60">
                    {l.memberCount}/{l.maxMembers} members · code {l.code}
                  </p>
                </div>
                <span className="text-base opacity-40">›</span>
              </Link>
            </li>
          ))}
        </ul>
      )}

      <CreateLeagueModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(league) => {
          setCreateOpen(false);
          navigate(`/leagues/${league.id}?just_created=1`);
        }}
      />
      <SignInModal open={signInOpen} onClose={() => setSignInOpen(false)} />
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-emerald-950 text-white">
      <header className="border-b border-white/10 px-4 py-3">
        <div className="mx-auto flex max-w-2xl items-center justify-between">
          <Link to="/" className="text-sm font-bold tracking-wider opacity-80 hover:opacity-100">
            CURVLE
          </Link>
          <div className="flex gap-2">
            <Link
              to="/play"
              className="rounded-full bg-white/10 px-3 py-1.5 text-xs font-semibold hover:bg-white/20"
            >
              Today&rsquo;s kick
            </Link>
            <Link
              to="/leaderboard"
              className="rounded-full bg-white/10 px-3 py-1.5 text-xs font-semibold hover:bg-white/20"
            >
              Leaderboard
            </Link>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-2xl px-4 py-6">{children}</main>
    </div>
  );
}
